import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import HeaderContent from "./HeaderContent";
import Progresbar from "./Progresbar";
import { MultiStepContainer } from "./Steps/MultiStepContainer";
import { Step1 } from "./Steps/Step1";
import { Step2 } from "./Steps/Step2";
import { Step3 } from "./Steps/Step3";
import { Step4 } from "./Steps/Step4";
import { Step5 } from "./Steps/Step5";
import { Step6 } from "./Steps/Step6";
import { Step7 } from "./Steps/Step7";
import { Step8 } from "./Steps/Step8";
import { Step9 } from "./Steps/Step9";
// import { Step10 } from "./Steps/Step10";
import { useSubmitWeeklyWarmapsAnswers } from "../../../../Controllers/Goals/UserGoalController";
import {
  getCurrentStep,
  formatWeek,
  initializeAnswersFromAPI,
  areAllQuestionsAnswered,
  hasAnswerChanged,
  prepareStepAnswersForSubmission,
  calculateProgressPercentage,
} from "../helper";
import { toast } from "react-toastify";

export default function WarmapContainer({
  initialWeeklyWarmapsQuestions = null,
}) {
  const navigate = useNavigate();

  // Single source of truth: data always comes from parent
  const weeklyWarmapsQuestions = initialWeeklyWarmapsQuestions;
  const hasWarmapData = !!weeklyWarmapsQuestions;

  const [headerStates, setHeaderState] = useState({
    progressPercentage: 0,
    title: "",
    description: "",
  });

  const [answers, setAnswers] = useState({});
  const [submittedAnswers, setSubmittedAnswers] = useState({});
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const submitMutation = useSubmitWeeklyWarmapsAnswers();

  // Initialize answers from API response (only on first load, don't overwrite user input)
  useEffect(() => {
    if (!weeklyWarmapsQuestions?.questions_by_steps) return;

    const {
      answers: initialAnswers,
      submittedAnswers: initialSubmittedAnswers,
    } = initializeAnswersFromAPI(weeklyWarmapsQuestions.questions_by_steps);

    setAnswers((prev) => {
      if (Object.keys(prev).length === 0) {
        return initialAnswers;
      }

      const merged = { ...prev };
      Object.keys(initialAnswers).forEach((key) => {
        const isEmpty =
          !merged[key] ||
          merged[key] === "" ||
          merged[key] === "[]" ||
          (Array.isArray(merged[key]) && merged[key].length === 0);

        if (isEmpty) {
          const apiValue = initialAnswers[key];
          if (
            apiValue !== undefined &&
            apiValue !== null &&
            apiValue !== "" &&
            apiValue !== "[]"
          ) {
            if (Array.isArray(apiValue)) {
              if (apiValue.length > 0) {
                merged[key] = apiValue;
              }
            } else {
              merged[key] = apiValue;
            }
          }
        }
      });
      return merged;
    });

    setSubmittedAnswers(initialSubmittedAnswers);
  }, [weeklyWarmapsQuestions]);

  // Get current step from API response
  const { totalSteps, currentStepIndex: apiCurrentStepIndex } = useMemo(() => {
    if (!weeklyWarmapsQuestions?.questions_by_steps) {
      return { totalSteps: 0, currentStepIndex: 0 };
    }
    return getCurrentStep(
      weeklyWarmapsQuestions.questions_by_steps,
      weeklyWarmapsQuestions?.session?.current_question_id ?? null,
    );
  }, [weeklyWarmapsQuestions]);

  // Initialize current step index from API
  useEffect(() => {
    if (apiCurrentStepIndex === undefined || !hasWarmapData) return;

    const isCompleted = weeklyWarmapsQuestions?.session?.is_completed === true;
    if (isCompleted) {
      setCurrentStepIndex(8); // Step 9 (index 8)
    } else {
      setCurrentStepIndex(apiCurrentStepIndex);
    }
  }, [
    apiCurrentStepIndex,
    hasWarmapData,
    weeklyWarmapsQuestions?.session?.is_completed,
  ]);

  // Progress percentage
  useEffect(() => {
    const progress = calculateProgressPercentage(currentStepIndex, 9);
    setHeaderState((prev) => ({
      ...prev,
      progressPercentage: progress,
    }));
  }, [currentStepIndex]);

  // Header info
  useEffect(() => {
    if (!hasWarmapData) return;

    setHeaderState((prev) => ({
      ...prev,
      title: "Weekly War Map",
      description: formatWeek(
        weeklyWarmapsQuestions.week?.start_date,
        weeklyWarmapsQuestions.week?.end_date,
      ),
    }));
  }, [hasWarmapData, weeklyWarmapsQuestions]);

  // Submit answers for a specific step
  const submitStepAnswers = async (stepIndex) => {
    if (!weeklyWarmapsQuestions?.questions_by_steps?.[stepIndex]) {
      return false;
    }

    const step = weeklyWarmapsQuestions.questions_by_steps[stepIndex];
    const stepQuestions = step.questions || [];

    // Skip submission if step has no questions (e.g., Step 6)
    if (stepQuestions.length === 0) {
      return true;
    }

    if (!areAllQuestionsAnswered(stepQuestions, answers)) {
      toast.error(
        "Please answer all questions in this step before proceeding.",
      );
      return false;
    }

    if (!hasAnswerChanged(stepQuestions, answers, submittedAnswers)) {
      return true;
    }

    const stepAnswers = prepareStepAnswersForSubmission(stepQuestions, answers);

    try {
      await submitMutation.mutateAsync({
        week_id: weeklyWarmapsQuestions.week?.id,
        answers: stepAnswers,
        from: weeklyWarmapsQuestions.week?.start_date,
        to: weeklyWarmapsQuestions.week?.end_date,
      });

      setSubmittedAnswers((prev) => {
        const updated = { ...prev };
        stepQuestions.forEach((question) => {
          updated[question.id] = answers[question.id];
        });
        return updated;
      });

      toast.success("Answers submitted successfully!");
      return true;
    } catch (error) {
      toast.error("Failed to submit answers. Please try again.");
      console.error("Submit error:", error);
      return false;
    }
  };

  const updateAnswer = useCallback((questionId, answer) => {
    setAnswers((prev) => {
      if (prev[questionId] === answer) return prev;
      return {
        ...prev,
        [questionId]: answer,
      };
    });
  }, []);

  const getSteps = (question_by_steps) => {
    if (!question_by_steps || question_by_steps.length === 0) return [];

    const StepComponents = [
      Step1,
      Step2,
      Step3,
      Step4,
      Step5,
      Step6,
      Step7,
      Step8,
      Step9,
      // Step10,
    ];

    const steps = [];
    for (let index = 0; index < 10; index++) {
      const StepComponent = StepComponents[index];
      if (!StepComponent) continue;

      const stepData = question_by_steps[index] || { questions: [] };

      steps.push(
        <StepComponent
          key={`step-${index}`}
          questions={stepData.questions || []}
          answers={answers}
          updateAnswer={updateAnswer}
          stepIndex={index}
          warmapQuestionResponse={weeklyWarmapsQuestions}
        />,
      );
    }

    return steps;
  };

  const steps = useMemo(() => {
    if (!weeklyWarmapsQuestions?.questions_by_steps) return [];
    return getSteps(weeklyWarmapsQuestions.questions_by_steps);
  }, [weeklyWarmapsQuestions, answers]);

  return (
    <div
      className="d-flex flex-column"
      style={{
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {!hasWarmapData ? (
        <div className="d-flex justify-content-center align-items-center h-100">
          <p className="text-body-b2" style={{ color: "#ccc" }}>
            Warmap data not available. Please open Weekly War Map from the Goals
            and Targets page.
          </p>
        </div>
      ) : (
        <>
          <div className="d-flex flex-column justify-content-between align-items-center">
            <HeaderContent
              title={headerStates.title}
              description={headerStates.description}
            />
            <Progresbar
              progressPercentage={headerStates.progressPercentage}
              style={{ marginTop: "24px" }}
            />
          </div>

          <div
            className="weekly-warmap-steps-container"
            style={{
              marginTop: "48px",
            }}
          >
            <MultiStepContainer
              steps={steps}
              currentStepIndex={currentStepIndex}
              setCurrentStepIndex={setCurrentStepIndex}
              totalSteps={totalSteps}
              submitStepAnswers={submitStepAnswers}
              weekId={weeklyWarmapsQuestions?.week?.id}
              isSubmitting={submitMutation.isPending}
              onComplete={async () => {
                try {
                  await submitStepAnswers(currentStepIndex);
                } catch (error) {
                  console.error("Error submitting final step:", error);
                }
                navigate("/goals-and-targets");
              }}
              isCompleted={weeklyWarmapsQuestions?.session?.is_completed}
            />
          </div>
        </>
      )}
    </div>
  );
}
